/**
 * @fileoverview Product Analytics - Funnel Analysis
 * @module features/analytics/funnels
 * 
 * @description
 * Define and analyze conversion funnels to understand user journeys,
 * identify drop-off points, and optimize conversion rates.
 * 
 * @useCases
 * - Track signup/onboarding completion rates
 * - Analyze checkout funnel performance
 * - Identify where users drop off in key flows
 * - Compare funnel performance across segments
 * - A/B test different funnel variations
 * 
 * @metrics
 * - Step-by-step conversion rates
 * - Drop-off rates at each step
 * - Time to complete each step
 * - Overall funnel conversion rate
 * - Funnel completion time
 * 
 * @chartData
 * - Funnel chart: Step-by-step visualization
 * - Bar chart: Conversion by step
 * - Line chart: Funnel performance over time
 * - Comparison: Funnel variants side-by-side
 * 
 * @example
 * ```typescript
 * const funnels = new Funnels(webId, endpoint, apiKey);
 * 
 * // Track funnel step
 * await funnels.trackStep({
 *   funnelId: 'checkout_funnel',
 *   step: 'add_payment',
 *   stepNumber: 3,
 *   userId: 'user_123'
 * });
 * 
 * // Get funnel analysis
 * const analysis = await funnels.analyze('checkout_funnel');
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { 
  FunnelStep,
  FunnelAnalysis,
  DateRangeFilter,
  TimeSeriesData
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Funnel definition configuration.
 */
export interface FunnelDefinition {
  /** Unique funnel identifier */
  funnelId: string;
  /** Funnel name for display */
  name: string;
  /** Funnel description */
  description?: string;
  /** Ordered list of step definitions */
  steps: Array<{
    /** Step name */
    name: string;
    /** Event(s) that trigger this step */
    events: string[];
    /** Step description */
    description?: string;
    /** Whether step is optional */
    optional?: boolean;
  }>;
}

/**
 * Configuration for tracking a funnel step.
 */
export interface FunnelStepConfig {
  /** Funnel identifier */
  funnelId: string;
  /** Step name or event */
  step: string;
  /** Step number (1-indexed) */
  stepNumber: number;
  /** User ID */
  userId?: string;
  /** Session ID */
  sessionId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Funnel step response from API.
 */
export interface FunnelStepResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    funnelId: string;
    step: string;
    tracked: boolean;
    previousStep?: string;
    timeFromPrevious?: number;
  };
  timestamp: string;
}

/**
 * Funnel analytics data for charts.
 */
export interface FunnelAnalyticsData {
  /** Funnel definition */
  funnel: FunnelDefinition;
  /** Analysis results */
  analysis: FunnelAnalysis;
  /** Step-by-step data for visualization */
  visualization: Array<{
    step: string;
    entered: number;
    completed: number;
    conversionRate: number;
    dropOffRate: number;
    avgTime: number;
  }>;
  /** Performance over time */
  trendData: TimeSeriesData;
}

/**
 * Funnel comparison data.
 */
export interface FunnelComparisonData {
  /** Base funnel data */
  baseline: FunnelAnalyticsData;
  /** Comparison funnel data */
  comparison: FunnelAnalyticsData;
  /** Step-by-step comparison */
  stepComparison: Array<{
    step: string;
    baselineRate: number;
    comparisonRate: number;
    difference: number;
    significant: boolean;
  }>;
}

// ============================================================================
// FUNNELS CLASS
// ============================================================================

/**
 * Funnels - Define and analyze conversion funnels.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides funnel analysis capabilities including:
 * - Funnel definition and management
 * - Step-by-step event tracking
 * - Conversion and drop-off analysis
 * - Time-to-completion tracking
 * - Segment-based funnel comparison
 * 
 * @example
 * ```typescript
 * const funnels = new Funnels(webId, endpoint, apiKey);
 * 
 * // Define a signup funnel
 * await funnels.define({
 *   funnelId: 'signup_funnel',
 *   name: 'User Signup',
 *   steps: [
 *     { name: 'Visit Landing', events: ['page_view_landing'] },
 *     { name: 'Click Signup', events: ['signup_clicked'] },
 *     { name: 'Submit Form', events: ['signup_submitted'] },
 *     { name: 'Verify Email', events: ['email_verified'] }
 *   ]
 * });
 * 
 * // Analyze funnel performance
 * const analysis = await funnels.analyze('signup_funnel', {
 *   range: 'last30days'
 * });
 * ```
 */
export class Funnels extends FlowGridTransport {
  /**
   * Defines a new funnel or updates existing.
   * 
   * @param config - Funnel definition
   * @returns Promise resolving to definition response
   * 
   * @example
   * ```typescript
   * await funnels.define({
   *   funnelId: 'activation_funnel',
   *   name: 'Activation Flow',
   *   steps: [
   *     { name: 'Visit Pricing', events: ['page_view'] },
   *     { name: 'Sign Up', events: ['signup'] },
   *     { name: 'Complete Onboarding', events: ['onboarding_complete'] },
   *     { name: 'Use Key Feature', events: ['feature_usage'] }
   *   ]
   * });
   * ```
   */
  async define(config: FunnelDefinition): Promise<FunnelStepResponse> {
    return await this.init("define_funnel", {
      type: "define_funnel",
      funnelId: config.funnelId,
      name: config.name,
      description: config.description,
      steps: config.steps,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a funnel step completion.
   * 
   * @param config - Funnel step configuration
   * @returns Promise resolving to step response
   * 
   * @example
   * ```typescript
   * // Track user progressing through checkout
   * await funnels.trackStep({
   *   funnelId: 'checkout_funnel',
   *   step: 'add_shipping_info',
   *   stepNumber: 3,
   *   userId: 'user_123',
   *   properties: { shippingMethod: 'express' }
   * });
   * ```
   */
  async trackStep(config: FunnelStepConfig): Promise<FunnelStepResponse> {
    return await this.init("funnel_step", {
      type: "funnel_step",
      funnelId: config.funnelId,
      step: config.step,
      stepNumber: config.stepNumber,
      userId: config.userId,
      sessionId: config.sessionId,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks funnel completion.
   * 
   * @param funnelId - Funnel identifier
   * @param userId - User who completed
   * @param properties - Completion properties
   * @returns Promise resolving to completion response
   */
  async trackComplete(
    funnelId: string,
    userId?: string,
    properties?: Record<string, unknown>
  ): Promise<FunnelStepResponse> {
    return await this.init("funnel_complete", {
      type: "funnel_complete",
      funnelId,
      userId,
      ...properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks funnel abandonment.
   * 
   * @param funnelId - Funnel identifier
   * @param lastStep - Last completed step
   * @param userId - User who abandoned
   * @param reason - Optional abandonment reason
   * @returns Promise resolving to abandonment response
   */
  async trackAbandon(
    funnelId: string,
    lastStep: string,
    userId?: string,
    reason?: string
  ): Promise<FunnelStepResponse> {
    return await this.init("funnel_abandon", {
      type: "funnel_abandon",
      funnelId,
      lastStep,
      userId,
      reason,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Analyzes funnel performance.
   * 
   * @param funnelId - Funnel identifier
   * @param filter - Date range filter
   * @returns Promise resolving to funnel analytics
   * 
   * @example
   * ```typescript
   * const analysis = await funnels.analyze('checkout_funnel', {
   *   range: 'last7days'
   * });
   * 
   * // Funnel visualization data
   * const funnelData = analysis.visualization.map(step => ({
   *   name: step.step,
   *   value: step.entered,
   *   conversion: step.conversionRate
   * }));
   * 
   * // Overall conversion rate
   * console.log(`Conversion: ${analysis.analysis.overallConversionRate}%`);
   * ```
   */
  async analyze(
    funnelId: string,
    filter?: DateRangeFilter
  ): Promise<FunnelAnalyticsData> {
    return await this.init("analyze_funnel", {
      type: "query",
      funnelId,
      filter: filter || { range: "last30days" },
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Compares two funnel periods or segments.
   * 
   * @param funnelId - Funnel identifier
   * @param baselineFilter - Baseline period filter
   * @param comparisonFilter - Comparison period filter
   * @returns Promise resolving to comparison data
   * 
   * @example
   * ```typescript
   * const comparison = await funnels.compare(
   *   'checkout_funnel',
   *   { range: 'last30days' },
   *   { range: 'last60days' }
   * );
   * 
   * // Side-by-side comparison chart
   * const comparisonData = comparison.stepComparison.map(s => ({
   *   step: s.step,
   *   baseline: s.baselineRate,
   *   comparison: s.comparisonRate
   * }));
   * ```
   */
  async compare(
    funnelId: string,
    baselineFilter: DateRangeFilter,
    comparisonFilter: DateRangeFilter
  ): Promise<FunnelComparisonData> {
    return await this.init("compare_funnel", {
      type: "query",
      funnelId,
      baselineFilter,
      comparisonFilter,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Lists all defined funnels.
   * 
   * @returns Promise resolving to funnel list
   */
  async list(): Promise<FunnelDefinition[]> {
    return await this.init("list_funnels", {
      type: "query",
      timestamp: new Date().toISOString()
    });
  }
}

export default Funnels;
